// ----------------------------------------------------------------------
// <copyright file="Izenda.LayoutComputerUtils.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------
!(function() {
  const { get, map, sortBy, forEach, placeholder } = require('lodash/fp');
  module.exports = {
    getGridLeft: function getGridLeft(left, cellWidth) {
      return Math.floor(left / cellWidth);
    },
    getGridTop: function getGridTop(top, cellHeight) {
      return Math.floor(top / cellHeight);
    },
    getGridWidth: function getGridWidth(width, cellWidth) {
      return Math.round(width / cellWidth);
    },
    getGridHeight: function getGridHeight(height, cellHeight) {
      return Math.round(height / cellHeight);
    },
    getDomLeft: function getDomLeft(gridX, cellWidth) {
      return (gridX || 0) * cellWidth;
    },
    getDomTop: function getDomTop(gridY, cellHeight) {
      return (gridY || 0) * cellHeight;
    },
    getDomWidth: function getDomWidth(gridW, cellWidth) {
      return (gridW || 0) * cellWidth;
    },
    getDomHeight: function getDomHeight(gridH, cellHeight) {
      return (gridH || 0) * cellHeight;
    },
    getDomPosition: function(gridX, gridY, gridW, gridH, cellWidth, cellHeight) {
      return {
        left: this.getDomLeft(gridX, cellWidth),
        top: this.getDomTop(gridY, cellHeight),
        width: gridW === Infinity ? gridW : this.getDomWidth(gridW, cellWidth),
        height: gridH === Infinity ? gridH : this.getDomWidth(gridH, cellHeight)
      };
    },
    getGridPosition: function getGridPosition(domX, domY, domW, domH, cellWidth, cellHeight) {
      return {
        left: this.getGridLeft(domX, cellWidth),
        top: this.getGridTop(domY, cellHeight),
        width: this.getGridWidth(domW, cellWidth),
        height: this.getGridWidth(domH, cellHeight)
      };
    },

    /**
     * Round width value of grid view by multiplied of grid's cell width.
     * @param  {Number} domW DOM element's width
     * @return {Number}      Rounded DOM element's width
     */
    roundWidth: function roundWidth(domW, cellWidth) {
      return Math.ceil(domW / cellWidth) * cellWidth;
    },

    /**
     * Round height value of grid view by multiplied of grid's cell height.
     * @param  {Number} domH DOM element's height
     * @return {Number}      Rounded DOM element's height
     */
    roundHeight: function roundHeight(domH, cellHeight) {
      return Math.ceil(domH / cellHeight) * cellHeight;
    },

    /**
     * Convert grid item state to layout item
     */
    convertToLayoutItem: function convertToLayoutItem(gridItem, cellWidth, cellHeight, ignorePx) {
      return ignorePx
        ? {
            id: gridItem.id,
            top: gridItem.gridTop,
            left: gridItem.gridLeft,
            width: gridItem.gridWidth,
            height: gridItem.gridHeight
          }
        : {
            id: gridItem.id,
            top: gridItem.pxTop ? this.getGridTop(gridItem.pxTop, cellHeight) : gridItem.gridTop,
            left: gridItem.pxLeft ? this.getGridLeft(gridItem.pxLeft, cellWidth) : gridItem.gridLeft,
            width: gridItem.pxWidth ? this.getGridWidth(gridItem.pxWidth, cellWidth) : gridItem.gridWidth,
            height: gridItem.pxHeight ? this.getGridHeight(gridItem.pxHeight, cellHeight) : gridItem.gridHeight
          };
    },

    convertToRect: function convertToRect(id, x, y, w, h) {
      return {
        id: id,
        x1: x,
        y1: y,
        x2: x + w,
        y2: y + h
      };
    },
    /**
     * Convert layout item to rectangle object
     */
    convertLayoutItemToRect: function convertLayoutItemToRect(cellWidth, cellHeight) {
      var computer = this;
      return function(item) {
        var itemWidth = item.autoSize ? computer.getGridWidth(item.pxWidth, cellWidth) : item.gridWidth;
        var itemHeight = item.autoSize ? computer.getGridHeight(item.pxHeight, cellHeight) : item.gridHeight;

        return computer.convertToRect(item.id, item.gridLeft, item.gridTop, itemWidth, itemHeight);
      };
    },
    /**
     * Convert all layout items to array of rectangle objects
     */
    convertLayoutsToRects: function convertLayoutsToRects(layouts, cellWidth, cellHeight) {
      var toRect = this.convertLayoutItemToRect(cellWidth, cellHeight);
      return map(toRect, layouts);
    },
    /**
     * Convert rectangle to layout item
     */
    convertRectToLayoutItem: function convertRectToLayoutItem(rect, cellWidth, cellHeight) {
      var width = rect.x2 - rect.x1;
      var height = rect.y2 - rect.y1;
      return {
        gridLeft: rect.x1,
        gridTop: rect.y1,
        gridWidth: width,
        gridHeight: height,
        pxTop: this.getDomTop(rect.y1, cellHeight),
        pxLeft: this.getDomLeft(rect.x1, cellWidth),
        pxWidth: this.getDomWidth(width, cellWidth),
        pxHeight: this.getDomHeight(height, cellHeight)
      };
    },
    /**
     * Calculate intersection area of two rectangles
     * @return {Number}              Intersection area
     */
    calculateIntersection: function calculateIntersection(checkingRect, runningRect) {
      // See http://stackoverflow.com/questions/9324339/how-much-do-two-rectangles-overlap
      var minX2 = Math.min(checkingRect.x2, runningRect.x2);
      var minY2 = Math.min(checkingRect.y2, runningRect.y2);

      var maxX1 = Math.max(checkingRect.x1, runningRect.x1);
      var maxY1 = Math.max(checkingRect.y1, runningRect.y1);

      return Math.max(0, minX2 - maxX1) * Math.max(0, minY2 - maxY1);
    },
    /**
     * Calculate interaction area ratio with checking rectangle
     */
    calculateIntersectionRatio: function calculateIntersectionRatio(checkingRect, runningRect) {
      var interArea = this.calculateIntersection(checkingRect, runningRect);
      var checkingWidth = checkingRect.x2 - checkingRect.x1;
      var checkingHeight = checkingRect.y2 - checkingRect.y1;
      var checkingArea = checkingWidth * checkingHeight;

      return checkingArea <= 0 ? 0.0 : interArea / checkingArea;
    },
    /**
     * Calculate spare offset at (x,y) position in the layout
     * @param  {Number} x             X value
     * @param  {Number} y             Y value
     * @param  {Array} occupiedRects  Array of occupied rectangles
     */
    calculateSpareOffset: function calculateSpareOffset(x, y, occupiedRects) {
      var x1 = x;
      var x2 = x;
      var y1 = y;
      var y2 = y + 1;

      var winnerX = placeholder(occupiedRects)
        .filter(function(rect) {
          return rect.x1 > x2 && rect.y1 < y + 3 && rect.y2 > y1;
        })
        .map(get('x1'))
        .min();

      var winnerY = placeholder(occupiedRects)
        .filter(function(rect) {
          return rect.y1 >= y2 && rect.x1 < (winnerX || Infinity) && rect.x2 > x1;
        })
        .map(get('y1'))
        .min();

      return {
        left: x,
        top: y,
        width: winnerX ? winnerX - x : Infinity,
        height: winnerY ? winnerY - y : Infinity
      };
    },
    /**
     * Check collision of two rectangle
     */
    collide: function collide(checkingRect) {
      var utils = this;
      return function(runningRect) {
        return utils.calculateIntersection(checkingRect, runningRect) > 0;
      };
    },

    /**
     * Check whether runningRect is swappable with checkingRect
     */
    swappable: function swappable(checkingRect, swapIntersectionRatio) {
      var utils = this;
      return function(runningRect) {
        var ratio = utils.calculateIntersectionRatio(checkingRect, runningRect);
        return ratio > swapIntersectionRatio;
      };
    },

    sortLayout: function sortLayout(layout) {
      return placeholder(layout).sortBy(['y1', 'x1']);
    },

    getAllCollisions: function getAllCollisions(checkingItem, layout) {
      return placeholder(layout).filter(this.collide(checkingItem));
    },

    getFirstCollision: function getFirstCollision(checkingItem, layout) {
      return placeholder(layout).find(this.collide(checkingItem));
    },

    getBottom: function getBottom(layout) {
      return placeholder(layout)
        .map(get('y2'))
        .max();
    },
    getRight: function getRight(layout) {
      return placeholder(layout)
        .map(get('x2'))
        .max();
    },
    compactLayout: function compactLayout(layout, oldLayoutItem, resize) {
      var index = 0,
        len = layout.length;
      var detectingItem, restItems, movingItems, spare, occupiedRects, detectingRect;
      var sorted = sortBy(['left', 'top'], layout);

      // If it doesn't specify an item, scan all collisions in layout and expand
      // the layout items to remove collisions.
      if (!oldLayoutItem) {
        // Detect any collided items
        for (; index < len; index++) {
          // Pick item for collision detecting sequentially
          detectingItem = sorted[index];

          // Detect collisions with the rest of items. Note that layout has been sorted
          // by position.
          restItems = placeholder(sorted)
            .slice(index + 1)
            .value();

          occupiedRects = placeholder(restItems)
            .map(
              function(item) {
                return this.convertToRect(item.id, item.left, item.top, item.width, item.height);
              }.bind(this)
            )
            .value();

          detectingRect = this.convertToRect(
            detectingItem.id,
            detectingItem.left,
            detectingItem.top,
            detectingItem.width,
            detectingItem.height
          );

          if (resize || this.getFirstCollision(detectingRect, occupiedRects)) {
            // The movement distance is the collision space. Take the spare space
            // as old layout item position will ensure that.
            spare = this.calculateSpareOffset(detectingItem.left, detectingItem.top, occupiedRects);
            movingItems = this.compactLayout(sorted, {
              id: detectingItem.id,
              top: spare.top,
              left: spare.left,
              width: spare.width,
              height: spare.height
            });

            // Move items to available position.
            placeholder(movingItems).forEach(function(move) {
              var updateItem = placeholder(sorted).find(['id', move.id]);
              updateItem.top = move.top;
              updateItem.left = move.left;
            });
          }
        }

        return sorted;
      }

      var currentLayoutItem = placeholder(sorted).find(['id', oldLayoutItem.id]);
      var currentLayoutItemIdx = placeholder(sorted).indexOf(currentLayoutItem);
      var checkingLayout = placeholder(sorted)
        .slice(currentLayoutItemIdx + 1)
        .value();

      var deltaX = currentLayoutItem.width - oldLayoutItem.width;
      var deltaY = currentLayoutItem.height - oldLayoutItem.height;
      var getHeight = deltaX > 0 ? Math.max : Math.min;

      var horizontalCheck = {
        id: currentLayoutItem.id,
        top: currentLayoutItem.top,
        left: currentLayoutItem.left,
        width: oldLayoutItem.width,
        height: getHeight(oldLayoutItem.height, currentLayoutItem.height)
      };
      var horizontalCollisions = this.getPossibleCollision(horizontalCheck, checkingLayout, collidesRight);
      var horizontalUpdates = this.moveItems(horizontalCollisions, checkingLayout, deltaX, false);

      forEach(function(horzUpdate) {
        var item = placeholder(checkingLayout).find(['id', horzUpdate.id]);
        item.left = horzUpdate.left;
      }, horizontalUpdates);

      var getWidth = deltaY > 0 ? Math.max : Math.min;
      var verticalCheck = {
        id: currentLayoutItem.id,
        top: currentLayoutItem.top,
        left: currentLayoutItem.left,
        width: getWidth(oldLayoutItem.width, currentLayoutItem.width),
        height: oldLayoutItem.height
      };
      var verticalCollisions = this.getPossibleCollision(verticalCheck, checkingLayout, collidesBottom);
      var verticalUpdates = this.moveItems(verticalCollisions, checkingLayout, deltaY, true);

      return horizontalUpdates.concat(verticalUpdates);

      function collidesRight(anchor, checking) {
        return (
          checking.left >= anchor.left + anchor.width &&
          checking.top < anchor.top + anchor.height &&
          checking.top + checking.height > anchor.top
        );
      }

      function collidesBottom(anchor, checking) {
        return (
          checking.top >= anchor.top + anchor.height &&
          checking.left < anchor.left + anchor.width &&
          checking.left + checking.width > anchor.left
        );
      }
    },

    moveItems: function(movingItems, layout, movingDistance, isVertical) {
      var movingIdx, movingLen, movingItem, finalMove, bound, boundDistance;
      var output = [],
        collisions;
      var move = isVertical ? moveY : moveX;
      var max = isVertical ? maxY : maxX;
      var distance = isVertical ? distanceY : distanceX;
      var collides = isVertical ? collidesTop : collidesLeft;

      var staticLayouts = placeholder(layout)
        .reject(function(item) {
          return placeholder(movingItems).findIndex(['id', item.id]) >= 0;
        })
        .value();

      for (movingIdx = 0, movingLen = movingItems.length; movingIdx < movingLen; movingIdx++) {
        movingItem = movingItems[movingIdx];

        if (movingDistance < 0) {
          // Movement becomes more sophisticated when collapsing item's position.
          // Before collapsing position, it needs to count available space for collapsing
          // and only collapses in the available space.
          collisions = this.getPossibleCollision(movingItem, staticLayouts, collides);
          bound = max(collisions) || 0;
          boundDistance = distance(movingItem, bound);
          movingDistance = Math.max(boundDistance, movingDistance);
        }
        finalMove = move(movingItem, movingDistance);
        staticLayouts.push(finalMove);
        output.push(finalMove);
      }
      return output;

      function maxX(items) {
        return placeholder(items)
          .map(function(item) {
            return item.left + item.width;
          })
          .max();
      }

      function maxY(items) {
        return placeholder(items)
          .map(function(item) {
            return item.top + item.height;
          })
          .max();
      }

      function distanceX(item, bound) {
        return bound - item.left;
      }

      function distanceY(item, bound) {
        return bound - item.top;
      }

      function moveX(item, distance) {
        return {
          id: item.id,
          top: item.top,
          left: item.left + distance,
          width: item.width,
          height: item.height
        };
      }

      function moveY(item, distance) {
        return {
          id: item.id,
          top: item.top + distance,
          left: item.left,
          width: item.width,
          height: item.height
        };
      }

      function collidesLeft(anchor, checking) {
        return (
          checking.left + checking.width <= anchor.left &&
          checking.top < anchor.top + anchor.height &&
          checking.top + checking.height > anchor.top
        );
      }

      function collidesTop(anchor, checking) {
        return (
          checking.top + checking.height <= anchor.top &&
          checking.left < anchor.left + anchor.width &&
          checking.left + checking.width > anchor.left
        );
      }
    },

    getPossibleCollision: function getPossibleCollision(item, layout, collides) {
      var utils = this;
      var groups = placeholder(layout)
        .partition(function(rect) {
          return collides(item, rect);
        })
        .value();

      var collisions = groups[0];
      var remains = groups[1];

      if (collisions.length === 0) {
        return [];
      }
      return placeholder(collisions).reduce(function(result, collided) {
        result.push(collided);
        return result.concat(utils.getPossibleCollision(collided, remains, collides));
      }, []);
    }
  };
})();



// WEBPACK FOOTER //
// ./ui/share/components/controls/GridLayout/Izenda.LayoutComputerUtils.js